import React from 'react';
import { Timeline, Tag, Typography, Button, Space } from 'antd';
import {
    PhoneOutlined, TeamOutlined, EnvironmentOutlined,
    MailOutlined, FileTextOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { CrmInteractionDTO, InteractionType } from '../types';

const { Text } = Typography;

const TYPE_ICON: Record<InteractionType, React.ReactNode> = {
    CALL: <PhoneOutlined />,
    MEETING: <TeamOutlined />,
    VISIT: <EnvironmentOutlined />,
    EMAIL: <MailOutlined />,
    NOTE: <FileTextOutlined />,
};

const TYPE_COLOR: Record<InteractionType, string> = {
    CALL: 'blue',
    MEETING: 'purple',
    VISIT: 'green',
    EMAIL: 'orange',
    NOTE: 'default',
};

interface Props {
    interactions: CrmInteractionDTO[];
    onEdit?: (interaction: CrmInteractionDTO) => void;
    onDelete?: (id: number) => void;
}

const InteractionTimeline: React.FC<Props> = ({ interactions, onEdit, onDelete }) => {
    if (interactions.length === 0) {
        return <Text type="secondary">No interactions recorded yet.</Text>;
    }

    return (
        <Timeline
            items={interactions.map(interaction => ({
                dot: TYPE_ICON[interaction.type],
                color: TYPE_COLOR[interaction.type],
                children: (
                    <div className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                            <Space>
                                <Tag color={TYPE_COLOR[interaction.type]}>
                                    {interaction.type}
                                </Tag>
                                <Text strong>{interaction.subject}</Text>
                            </Space>
                            <Space>
                                <Text type="secondary" className="text-xs">
                                    {interaction.interactionDate}
                                    {interaction.officer && ` · ${interaction.officer}`}
                                </Text>
                                {onEdit && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => onEdit(interaction)}
                                    />
                                )}
                                {onDelete && interaction.id && (
                                    <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => onDelete(interaction.id!)}
                                    />
                                )}
                            </Space>
                        </div>
                        {interaction.notes && (
                            <Text type="secondary" className="block text-sm whitespace-pre-wrap">
                                {interaction.notes}
                            </Text>
                        )}
                        {interaction.outcome && (
                            <Text className="block text-sm mt-1">
                                <strong>Outcome:</strong> {interaction.outcome}
                            </Text>
                        )}
                        {interaction.followUpDate && (
                            <Space className="mt-1">
                                {interaction.followUpDone
                                    ? <Tag icon={<CheckCircleOutlined />} color="success">Follow-up done</Tag>
                                    : <Tag color="warning">Follow-up: {interaction.followUpDate}</Tag>
                                }
                            </Space>
                        )}
                    </div>
                ),
            }))}
        />
    );
};

export default InteractionTimeline;
